import {
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {InputArray} from "@components/input-array";
import {AddIcon, DeleteIcon} from "@components/icons";
import {parseSensorColumns, translate} from "@util";
import {SensorFilter, SensorMultivalue} from "@core/model";

// riferimento stabile: un nuovo [] a ogni render resetterebbe InputArray
const EMPTY: any[] = [];

// accetta anche il vecchio formato con un singolo filtro
const parseFilters = (filters: unknown): SensorFilter[] => {
  if (Array.isArray(filters)) return filters;
  if (filters && typeof filters === "object") return [filters as SensorFilter];
  return [];
};

// Filtri sui valori delle colonne del sensore: index è la posizione della
// colonna nel valore della misura (la prima colonna è il timestamp).
// Ogni colonna può avere un solo filtro.
const SensorValueFilter = ({
  value: sensor,
  onChange,
  disabled,
}: {
  value: SensorMultivalue;
  onChange: (filters: SensorFilter[] | null) => void;
  disabled?: boolean;
}) => {
  const filters = parseFilters(sensor?.filters);
  const options = parseSensorColumns(sensor?.columns)
    .map((label, index) => ({index, label}))
    .slice(1);
  const freeOptions = options.filter(
    o => !filters.some(f => f.index === o.index),
  );

  const emitChange = (newFilters: SensorFilter[]) => {
    onChange(newFilters.length > 0 ? newFilters : null);
  };

  const handleAdd = () => {
    emitChange([...filters, {index: freeOptions[0].index, value: []}]);
  };

  const handleRemove = (position: number) => {
    emitChange(filters.filter((_, i) => i !== position));
  };

  const handleColumnChange = (position: number, index: number) => {
    emitChange(filters.map((f, i) => (i === position ? {...f, index} : f)));
  };

  const handleValuesChange = (position: number, value: string) => {
    const newValues = value ? value.split(",") : [];
    emitChange(
      filters.map((f, i) => (i === position ? {...f, value: newValues} : f)),
    );
  };

  return (
    <Stack mt={2} mb={1}>
      <Typography variant="body1">
        {translate("entity.sensor.valueFilter.header")}
      </Typography>
      {filters.map((filter, position) => {
        const known = options.some(o => o.index === filter.index);
        return (
          <Paper key={filter.index} sx={{p: 2, mt: 1}}>
            <Stack direction="row" gap={1} alignItems="flex-start">
              <Stack gap={2} flex={1} minWidth={0}>
                <TextField
                  select
                  variant="standard"
                  size="small"
                  label={translate("entity.sensor.valueFilter.column")}
                  value={known ? filter.index : ""}
                  disabled={disabled || options.length === 0}
                  onChange={e =>
                    handleColumnChange(position, Number(e.target.value))
                  }>
                  {options.map(o => (
                    <MenuItem
                      key={o.index}
                      value={o.index}
                      // una colonna già filtrata non può essere scelta di nuovo
                      disabled={
                        o.index !== filter.index &&
                        filters.some(f => f.index === o.index)
                      }>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
                <InputArray
                  label="entity.sensor.valueFilter.values"
                  value={filter.value ?? EMPTY}
                  disabled={disabled}
                  onChange={e => handleValuesChange(position, e.target.value)}
                />
              </Stack>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => handleRemove(position)}
                aria-label={translate("common.action.delete")}>
                <DeleteIcon />
              </IconButton>
            </Stack>
          </Paper>
        );
      })}
      <Button
        size="small"
        startIcon={<AddIcon />}
        sx={{mt: 1, alignSelf: "flex-start"}}
        disabled={disabled || freeOptions.length === 0}
        onClick={handleAdd}>
        {translate("entity.sensor.valueFilter.add")}
      </Button>
    </Stack>
  );
};

export {SensorValueFilter};
